/**
 * Adapptio Core Library
 *
 * @package lib-core
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import {
	HttpRequest,
	HTTP_METHOD,
	IHttpHeaders,
	IHttpResponse,
	TRequestBody,
	TRequestQuery
} from "./HttpRequest";
import { IApiCsfrTokenResponse, IApiResponse } from "../api";

const CSFR_TTL_MS = 15 * 60 * 1000; // 15 minutes
const CSFR_MAX_RETRIES = 1;

declare global {
	interface Window {
		APP_DEBUG: boolean;
	}
}

export interface HttpClientOptions {
	useCsfr?: boolean;
}

/**
 * HTTP Client class
 */
export class HttpClient {
	/** Server URL to prefix path with */
	private serverUrl: string;

	/** Global headers */
	private headers: IHttpHeaders = {};

	/** Global timeout */
	private timeoutMs = 300000;

	/** CSFR token */
	private useCsfr: boolean;
	private csfrToken: string|null = null;
	private csfrTokenPromise: Promise<string>|null = null;
	private csfrRefreshedAt: number|null = null;

	/** Event emitted when request ends up with an error */
	public onError = createEventEmitter<Error>();

	/**
	 * HTTP Client class constructor
	 *
	 * @param serverUrl Base server URL to prefix path with
	 */
	public constructor(serverUrl = "", headers: IHttpHeaders = {}, opts?: HttpClientOptions) {
		this.serverUrl = serverUrl;
		this.headers = headers;
		this.useCsfr = opts?.useCsfr ?? false;
	}

	/**
	 * Tries to resolve and return valid CSFR token
	 * @returns Promise<string> CSFR token
	 */
	private async getCsfrToken(): Promise<string> {
		if (this.csfrToken && this.csfrRefreshedAt && Date.now() - this.csfrRefreshedAt < CSFR_TTL_MS) {
			return this.csfrToken;
		}

		if (this.csfrTokenPromise) {
			return this.csfrTokenPromise;
		}

		// eslint-disable-next-line no-async-promise-executor
		this.csfrTokenPromise = new Promise<string>(async (resolve, reject) => {
			console.debug("[HTTP Client] Fetching new CSFR token...");

			try {
				const req = new HttpRequest<IApiResponse<IApiCsfrTokenResponse>>(
					HTTP_METHOD.POST,
					`${this.serverUrl}/csfr`,
					undefined,
					undefined,
					this.headers,
					this.timeoutMs
				);

				const res = await req.send();

				this.csfrToken = res.body.data.token;
				this.csfrRefreshedAt = Date.now();
				console.debug("[HTTP Client] Fetched new CSFR token.");
				resolve(this.csfrToken);
			} catch (err) {
				console.error("[HTTP Client] Failed to get CSFR token:", err);
				reject(err);
			} finally {
				this.csfrTokenPromise = null;
			}
		});

		return this.csfrTokenPromise;
	}

	private clearCsfrToken(): void {
		this.csfrToken = null;
		this.csfrRefreshedAt = null;
		this.csfrTokenPromise = null;
	}

	public getServerUrl(): string {
		return this.serverUrl;
	}

	/**
	 * Sets the global header
	 *
	 * @param name Name
	 * @param value Value
	 */
	public setHeader(name: string, value: string): void {
		this.headers[name] = value;
	}

	/**
	 * Returns globally set header by name
	 *
	 * @param name Name
	 */
	public getHeader(name: string): string {
		return this.headers[name];
	}

	/**
	 * Returns all headers
	 */
	public getAllHeaders(): IHttpHeaders {
		return this.headers;
	}

	/**
	 * Creates an instance of HttpRequest and sends it immediately
	 *
	 * @param method Method of request
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	private async sendRequest<TResBody>(
		method: HTTP_METHOD,
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number,
		retries = 0
	): Promise<IHttpResponse<TResBody>> {
		const csfrToken = this.useCsfr
			? await this.getCsfrToken()
			: null;

		const req = new HttpRequest<TResBody>(
			method,
			`${this.serverUrl}${path}`,
			query,
			body,
			{
				...this.headers,
				...(headers || {}),
				...(csfrToken ? { "X-CSFR-Token": csfrToken } : {})
			},
			timeout !== undefined ? timeout : this.timeoutMs
		);

		if (window.APP_DEBUG) {
			console.debug("[HTTP Client]: Request", req);
		}

		try {
			const res = await req.send();

			if (window.APP_DEBUG) {
				console.debug("[HTTP Client] Response:", res);
			}

			return res;
		} catch (err) {
			if (window.APP_DEBUG) {
				console.debug("[HTTP Client] Error:", err);
			}

			if (err instanceof Object && 'additionalProps' in err && 'reasonCode' in err.additionalProps && err.additionalProps.reasonCode === 'InvalidCsfrToken') {
				const willRetry = retries < CSFR_MAX_RETRIES;
				console.debug("[HTTP Client] CSFR token invalid, clearing token", willRetry ? "and will retry." : "and will NOT retry.");

				this.clearCsfrToken();

				if (willRetry) {
					return this.sendRequest<TResBody>(method, path, query, body, headers, timeout, retries + 1);
				}
			}

			emitEvent(this.onError, err);

			throw err;
		}
	}

	/**
	 * Shorthand method that creates GET HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async get<TResBody>(
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<TResBody>> {
		return this.sendRequest<TResBody>(HTTP_METHOD.GET, path, query, body, headers, timeout);
	}

	/**
	 * Shorthand method that creates POST HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async post<TResBody>(
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<TResBody>> {
		return this.sendRequest<TResBody>(HTTP_METHOD.POST, path, query, body, headers, timeout);
	}

	/**
	 * Shorthand method that creates PUT HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async put<TResBody>(
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<TResBody>> {
		return this.sendRequest<TResBody>(HTTP_METHOD.PUT, path, query, body, headers, timeout);
	}

	/**
	 * Shorthand method that creates PATCH HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async patch<TResBody>(
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<TResBody>> {
		return this.sendRequest<TResBody>(HTTP_METHOD.PATCH, path, query, body, headers, timeout);
	}

	/**
	 * Shorthand method that creates DELETE HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async delete<TResBody>(
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<TResBody>> {
		return this.sendRequest<TResBody>(HTTP_METHOD.DELETE, path, query, body, headers, timeout);
	}

	/**
	 * Shorthand method that creates HEAD HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async head(
		path: string,
		query?: TRequestQuery,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<never>> {
		return this.sendRequest<never>(HTTP_METHOD.HEAD, path, query, null, headers, timeout);
	}

	/**
	 * Shorthand method that creates OPTIONS HttpRequest and sends it immediately
	 *
	 * @param path Relative path
	 * @param query Query of request
	 * @param body Body (data) to send
	 * @param headers Request headers
	 * @param timeout Request timeout
	 */
	public async options<TResBody>(
		path: string,
		query?: TRequestQuery,
		body?: TRequestBody,
		headers?: IHttpHeaders,
		timeout?: number
	): Promise<IHttpResponse<TResBody>> {
		return this.sendRequest<TResBody>(HTTP_METHOD.OPTIONS, path, query, body, headers, timeout);
	}
}
